// Content for the industry pages (rendered by <IndustryPage />).

export interface IndustryCapability {
  title: string;
  text: string;
  image?: string;
  imageAlt?: string;
}

export interface IndustryItem {
  title: string;
  text: string;
  link?: string;
}

export interface IndustryData {
  name: string;
  banner: string;
  subtitle: string;
  intro: string;
  chips?: string[];
  flow: [string, string, string];
  capabilitiesTitle: string;
  capabilities: IndustryCapability[];
  also?: { title: string; items: IndustryItem[] };
  closing?: { title: string; items: IndustryItem[] };
  cta: string;
}

export const INDUSTRIES: Record<string, IndustryData> = {
  edtech: {
    name: 'EdTech',
    banner: '/Assets/Edtech/edtech-banner-image.png',
    subtitle: 'Learning platforms, analytics and AI for modern institutions.',
    intro: 'We help educational institutions improve how they review programs, deliver learning and measure outcomes.',
    chips: [
      'Collaborative education',
      'Configurable LMS platforms',
      'Adaptive learning engines',
      'Interactive content',
      'Mobile learning',
      'Data analytics & reporting',
      'Customizable content engineering',
    ],
    flow: ['Learning data', 'Insight', 'Better outcomes'],
    capabilitiesTitle: 'EdTech Development Services',
    capabilities: [
      {
        title: 'Real-Time Analytics & Reporting',
        text: 'Track student and teacher performance and activity in one place, with insights that support better decisions.',
        image: '/Assets/Edtech/real-time-analytics-section-image.png',
        imageAlt: 'Real-time analytics and reporting software for education',
      },
      {
        title: 'Staff & Teacher Management',
        text: 'Streamline administration, performance tracking and communication between staff and administrators.',
        image: '/Assets/Edtech/staff-teacher-management-soft-section-image.png',
        imageAlt: 'Staff and teacher management software',
      },
      {
        title: 'Collaboration & Video',
        text: 'Customizable WebRTC scheduling and virtual meetings for e-learning, training, assessments and group work.',
        image: '/Assets/Edtech/real-time-collaboration-section-image.png',
        imageAlt: 'Real-time collaboration and video communication',
      },
      {
        title: 'Educational Apps',
        text: 'Cross-platform mobile apps with video calling, live classrooms and rich media.',
        image: '/Assets/Edtech/educational-app-development-section-image.png',
        imageAlt: 'Educational mobile app development',
      },
      {
        title: 'Content Management',
        text: 'One system for faculty and admins to publish video, audio, images and interactive content.',
        image: '/Assets/Edtech/content-creation-section-image.png',
        imageAlt: 'Content creation and management software',
      },
      {
        title: 'LMS Integration',
        text: 'Connect your learning management system with the tools and platforms your institution relies on.',
        image: '/Assets/Edtech/lms-integration-section-image.png',
        imageAlt: 'Learning management system integration',
      },
    ],
    closing: {
      title: 'Beyond the platform',
      items: [
        { title: 'Catalyst Curriculum', text: 'Evidence-linked support for ongoing academic program review.', link: '/products/#curriculum' },
        { title: 'Catalyst Workforce', text: 'Labor-market and funding signals for program planning.', link: '/products/#workforce' },
      ],
    },
    cta: 'Planning your next education platform?',
  },

  'finance-accounting': {
    name: 'Finance & Accounting',
    banner: '/Assets/Finance & Accounting/finance-accounting-banner-image.png',
    subtitle: 'Faster, more transparent finance operations.',
    intro: 'We help finance teams improve efficiency, transparency and response times, with people in control of decisions.',
    flow: ['Transactions', 'Risk signals', 'Approved action'],
    capabilitiesTitle: 'What We Build',
    capabilities: [
      {
        title: 'Agentic AI for Financial Operations',
        text: 'Agents integrated with your finance applications to support decisions, with audit trails and human approval for sensitive actions.',
        image: '/Assets/Finance & Accounting/agentic-ai-solutions-section-image.png',
        imageAlt: 'Agentic AI solutions for financial operations',
      },
      {
        title: 'Intelligent CRM',
        text: 'Cloud CRM integrated with microservices, voice technology and AI for better client and financial management.',
        image: '/Assets/Finance & Accounting/intelligent-crm-solutions-section-image.png',
        imageAlt: 'Intelligent CRM solutions',
      },
      {
        title: 'Robotic Process Automation',
        text: 'RPA bots that take over labor-intensive finance processes and free teams for work that needs judgment.',
        image: '/Assets/Finance & Accounting/robotic-process-automation-section-image.png',
        imageAlt: 'Robotic process automation for finance',
      },
    ],
    also: {
      title: 'Payment fraud protection, with Trustmi',
      items: [
        { title: 'Automated Payment Fraud Protection', text: 'Safeguards the entire B2B payment lifecycle against socially engineered attacks, with less manual effort.' },
        { title: 'Real-Time Data Correlation', text: 'Validates transactions in real time by combining data from your financial systems with other critical signals.' },
        { title: 'Comprehensive Behavioral AI', text: 'Analyzes patterns across vendors, payments, emails and documents to detect sophisticated tactics.' },
      ],
    },
    cta: 'Ready to modernize your finance operations?',
  },

  healthcare: {
    name: 'Healthcare',
    banner: '/Assets/Healthcare/healthcare-banner-image.png',
    subtitle: 'AI that gives care teams their time back.',
    intro: 'Custom AI agents and software that reduce administrative load and support clinicians, with privacy, access controls and audit logging suited to regulated healthcare.',
    chips: ['Hospitals', 'Clinics', 'Dental', 'Physical therapy', 'Home care', 'Healthcare insurance'],
    flow: ['Patient data', 'Clinical insight', 'Care action'],
    capabilitiesTitle: 'Where AI Helps',
    capabilities: [
      {
        title: 'Streamlining Administrative Operations',
        text: 'AI-assisted scheduling and caregiver matching, plus documentation assistance that cuts time spent on clinical notes.',
        image: '/Assets/Healthcare/streamlining-administrative-operations-section-image.png',
        imageAlt: 'Streamlining administrative operations in healthcare',
      },
      {
        title: 'Personalized & Proactive Care',
        text: 'Tailored care plans for clinician review, and remote monitoring that flags anomalies early.',
        image: '/Assets/Healthcare/personal-care-section-image.png',
        imageAlt: 'Personalized and proactive patient care',
      },
      {
        title: 'Alleviating Staffing Challenges',
        text: 'Automate routine tasks, and support recruitment and personalized training for caregivers.',
        image: '/Assets/Healthcare/staffing-challanges-section-image.png',
        imageAlt: 'Alleviating healthcare staffing challenges',
      },
      {
        title: 'Improving Operational Efficiency',
        text: 'Predictive analytics to forecast patient needs and allocate resources, plus quality monitoring.',
        image: '/Assets/Healthcare/operational-efficiency-section-image.png',
        imageAlt: 'Improving healthcare operational efficiency',
      },
    ],
    closing: {
      title: 'Our Engagement Models',
      items: [
        { title: 'Dedicated Development Team', text: 'A focused team applying modern AI and software technologies to your solution.' },
        { title: 'Team Extension', text: 'Add the exact expertise your project needs to your existing team.' },
        { title: 'Project-based Model', text: 'A defined scope and outcomes, delivered in close collaboration.' },
      ],
    },
    cta: 'Let us support your care teams.',
  },



  pharmaceutical: {
    name: 'Pharmaceutical',
    banner: '/Assets/Pharmaceuticals/pharmaceutical-banner-image.png',
    subtitle: 'Faster insight, with safety and compliance in view.',
    intro: 'AI for pharmaceutical teams that improves efficiency and supports safety and compliance, with human review for regulated decisions.',
    flow: ['Research & trial data', 'Evidence', 'Informed decision'],
    capabilitiesTitle: 'Use Cases',
    capabilities: [
      {
        title: 'Drug Discovery & Development',
        text: 'Virtual screening and similarity analysis to prioritize candidates faster. Results guide scientists; laboratory validation still decides.',
        image: '/Assets/Pharmaceuticals/drug-discovery-development-section-image.png',
        imageAlt: 'AI in drug discovery and development',
      },
      {
        title: 'Clinical Trials',
        text: 'Machine learning across trial data to surface patterns that human review alone can miss.',
        image: '/Assets/Pharmaceuticals/clinical-trials-section-image.png',
        imageAlt: 'AI for clinical trials',
      },
      {
        title: 'Pharmaceutical Manufacturing',
        text: 'Automation for packaging inspection and quality control, within validated processes.',
        image: '/Assets/Pharmaceuticals/pharmaceutical-manufacturing-section-image.png',
        imageAlt: 'AI in pharmaceutical manufacturing',
      },
      {
        title: 'Pharmaceutical Data Management',
        text: 'Process large data volumes faster and see trends, with sources available for review.',
        image: '/Assets/Pharmaceuticals/pharmaceutical-data-management-section-image.png',
        imageAlt: 'Pharmaceutical data management',
      },
      {
        title: 'Pharmacovigilance',
        text: 'Mine adverse-event reports worldwide and draft safety-report input for expert review.',
        image: '/Assets/Pharmaceuticals/pharmacovugilance-section-image.png',
        imageAlt: 'AI for pharmacovigilance',
      },
    ],
    also: {
      title: 'Also included',
      items: [
        { title: 'Data Analysis & Processing', text: 'Insights from text documents and social media, organized around key business questions.' },
        { title: 'Natural Language Processing', text: 'Summarize large volumes of content and find the most relevant information.' },
        { title: 'Computer Vision & Image Analysis', text: 'Insights from images, including production-line quality assurance.' },
        { title: 'AI Virtual Agents', text: 'Chatbots to virtual agents that can work 24/7 for sales and support.' },
      ],
    },
    closing: {
      title: 'For research portfolios',
      items: [
        { title: 'Catalyst Research', text: 'Portfolio decisions across research projects: continue, redirect, advance or stop.', link: '/products/#research' },
      ],
    },
    cta: 'Let us help your teams move faster.',
  },

};
